// Étapes d'une scène : un nom d'action du catalogue + des paramètres typés.
// Liste FERMÉE : une action inconnue est refusée.
//
// Calendrier (cf. `scheduleScene`) : une étape démarre quand TOUTES les
// étapes précédentes sont finies, sauf `with` (en même temps qu'une étape
// précédente) ou `atSentence` (au début d'une phrase de la narration) ;
// `delayMs` décale. Une étape interactive (`question.ask`, `tool.open`) met la
// scène en attente de l'élève.

import { z } from 'zod';
import { JRAF_LIMITS as L } from '../limits';
import {
  displayTextSchema,
  easingSchema,
  jrafIdSchema,
  numExprSchema,
  paintSchema,
  pointSchema,
  TOOL_ID_RE,
  valueExprSchema,
} from './primitives';

const targetSchema = z.union([jrafIdSchema, z.array(jrafIdSchema).min(1).max(L.maxTargets)]);

/** Ancrage commun à toutes les étapes. */
const anchorFields = {
  id: jrafIdSchema,
  /** démarre en même temps que cette étape PRÉCÉDENTE de la scène */
  with: jrafIdSchema.optional(),
  /** démarre au début de la phrase n (0 = première) de la narration */
  atSentence: z.number().int().min(0).max(50).optional(),
  delayMs: z.number().int().min(0).max(L.maxDelayMs).optional(),
};

/** Champs des étapes animées (durée et easing). */
const timedFields = {
  ...anchorFields,
  durationMs: z.number().int().min(0).max(L.maxStepDurationMs).optional(),
  easing: easingSchema.optional(),
};

export const SHOW_EFFECTS = ['fade', 'draw', 'pop', 'none'] as const;

export const showStepSchema = z
  .object({
    ...timedFields,
    action: z.literal('object.show'),
    target: targetSchema,
    effect: z.enum(SHOW_EFFECTS).default('fade'),
  })
  .strict();

export const hideStepSchema = z
  .object({
    ...timedFields,
    action: z.literal('object.hide'),
    target: targetSchema,
    effect: z.enum(['fade', 'none']).default('fade'),
  })
  .strict();

/** Décalage ABSOLU de l'objet par rapport à sa position de départ. */
export const moveStepSchema = z
  .object({
    ...timedFields,
    action: z.literal('object.move'),
    target: targetSchema,
    dx: numExprSchema,
    dy: numExprSchema,
  })
  .strict();

export const scaleStepSchema = z
  .object({
    ...timedFields,
    action: z.literal('object.scale'),
    target: targetSchema,
    scale: z.number().min(0.05).max(20),
    /** centre de l'agrandissement ; défaut : l'ancre de l'objet */
    origin: pointSchema.optional(),
  })
  .strict();

export const rotateStepSchema = z
  .object({
    ...timedFields,
    action: z.literal('object.rotate'),
    target: targetSchema,
    /** degrés, sens horaire à l'écran */
    angle: z.number().min(-3600).max(3600),
    origin: pointSchema.optional(),
  })
  .strict();

export const styleStepSchema = z
  .object({
    ...timedFields,
    action: z.literal('object.style'),
    target: targetSchema,
    fill: paintSchema.optional(),
    stroke: paintSchema.optional(),
    strokeWidth: z.number().min(0).max(20).optional(),
    opacity: z.number().min(0).max(1).optional(),
  })
  // « au moins une propriété » : vérifié par la couche de cohérence
  // (discriminatedUnion n'accepte pas un schéma raffiné).
  .strict();

/** Change le texte d'un objet `text` ou l'étiquette d'un `point`. */
export const labelStepSchema = z
  .object({
    ...timedFields,
    action: z.literal('label.set'),
    target: jrafIdSchema,
    text: displayTextSchema(L.maxTextChars),
  })
  .strict();

/** Passe d'une écriture à une autre (égalité vérifiée par le noyau, lot 3). */
export const formulaTransformStepSchema = z
  .object({
    ...timedFields,
    action: z.literal('formula.transform'),
    target: jrafIdSchema,
    to: z.string().trim().min(1).max(L.maxTexChars),
    /** nom du geste affiché pendant la transformation (« Développer ») */
    label: displayTextSchema(60).optional(),
    flash: z.boolean().default(true),
  })
  .strict();

export const valueSetStepSchema = z
  .object({
    ...timedFields,
    action: z.literal('value.set'),
    target: jrafIdSchema,
    expr: valueExprSchema,
  })
  .strict();

/** Éclaire un objet un instant (halo doré), sans le changer : « regarde ici ». */
export const highlightStepSchema = z
  .object({
    ...anchorFields,
    action: z.literal('object.highlight'),
    target: targetSchema,
    durationMs: z.number().int().min(300).max(8000).optional(),
  })
  .strict();

export const waitStepSchema = z
  .object({
    ...anchorFields,
    action: z.literal('timeline.wait'),
    durationMs: z.number().int().min(1).max(L.maxStepDurationMs),
  })
  .strict();

/** Comportement d'une étape interactive dans la vidéo exportée. */
export const exportBehaviorSchema = z.discriminatedUnion('mode', [
  z
    .object({
      mode: z.literal('auto-reveal'),
      afterMs: z.number().int().min(1000).max(10_000).default(3000),
    })
    .strict(),
  z.object({ mode: z.literal('cta') }).strict(),
  z.object({ mode: z.literal('skip') }).strict(),
]);
export type ExportBehavior = z.infer<typeof exportBehaviorSchema>;

/** QCM à une bonne réponse (partie interactive, payante). */
export const questionStepSchema = z
  .object({
    ...anchorFields,
    action: z.literal('question.ask'),
    prompt: displayTextSchema(400),
    choices: z.array(displayTextSchema(120)).min(2).max(L.maxChoices),
    correct: z.number().int().min(0),
    hint: displayTextSchema(300).optional(),
    explanation: displayTextSchema(600),
    exportBehavior: exportBehaviorSchema,
  })
  .strict();

/** Ouvre le vrai outil de la boîte à outils (partie interactive, payante). */
export const toolOpenStepSchema = z
  .object({
    ...anchorFields,
    action: z.literal('tool.open'),
    toolId: z.string().regex(TOOL_ID_RE, 'identifiant d’outil invalide'),
    prompt: displayTextSchema(200),
    exportBehavior: exportBehaviorSchema,
  })
  .strict();

export const jrafStepSchema = z.discriminatedUnion('action', [
  showStepSchema,
  hideStepSchema,
  moveStepSchema,
  scaleStepSchema,
  rotateStepSchema,
  styleStepSchema,
  labelStepSchema,
  formulaTransformStepSchema,
  valueSetStepSchema,
  highlightStepSchema,
  waitStepSchema,
  questionStepSchema,
  toolOpenStepSchema,
]);
export type JrafStep = z.infer<typeof jrafStepSchema>;
export type JrafAction = JrafStep['action'];

export const JRAF_ACTIONS = [
  'object.show',
  'object.hide',
  'object.move',
  'object.scale',
  'object.rotate',
  'object.style',
  'label.set',
  'formula.transform',
  'value.set',
  'object.highlight',
  'timeline.wait',
  'question.ask',
  'tool.open',
] as const satisfies readonly JrafAction[];

/** Actions de la partie interactive (payante), permises seulement dans une scène `interactive`. */
export const INTERACTIVE_ACTIONS: readonly JrafAction[] = ['question.ask', 'tool.open'];

export function isInteractiveStep(
  step: JrafStep,
): step is Extract<JrafStep, { action: 'question.ask' | 'tool.open' }> {
  return step.action === 'question.ask' || step.action === 'tool.open';
}

/** Durée par défaut d'une étape animée (ms). Les étapes interactives attendent l'élève. */
export const DEFAULT_DURATION_MS: Record<JrafAction, number> = {
  'object.show': 600,
  'object.hide': 400,
  'object.move': 800,
  'object.scale': 600,
  'object.rotate': 800,
  'object.style': 500,
  'label.set': 300,
  'formula.transform': 900,
  'value.set': 800,
  'object.highlight': 1800,
  'timeline.wait': 0,
  'question.ask': 0,
  'tool.open': 0,
};

export function stepTargets(step: JrafStep): string[] {
  if (!('target' in step)) return [];
  return Array.isArray(step.target) ? step.target : [step.target];
}
